import React, { forwardRef, useId, useState } from "react";

type DgaSearchBoxProps = {
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  className?: string;
  dir?: "rtl" | "ltr";
  micAriaLabel?: string;
};

const DgaSearchBox = forwardRef<HTMLInputElement, DgaSearchBoxProps>(function DgaSearchBox(
  {
    placeholder = "",
    value: valueProp,
    defaultValue = "",
    onChange,
    disabled = false,
    readOnly = false,
    name = "q",
    id: idProp,
    className = "",
    dir = "rtl",
    micAriaLabel = "البحث الصوتي",
  },
  ref
) {
  const reactId = useId();
  const inputId = idProp ?? `dga-search-${reactId}`;
  const isControlled = valueProp !== undefined;
  const [inner, setInner] = useState(defaultValue);
  const value = isControlled ? valueProp! : inner;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setInner(e.target.value);
    onChange?.(e);
  };

  const handleClear = () => {
    if (disabled || readOnly) return;
    if (!isControlled) setInner("");
    const fake = {
      target: { value: "", name } as HTMLInputElement,
      currentTarget: { value: "", name } as HTMLInputElement,
    } as React.ChangeEvent<HTMLInputElement>;
    onChange?.(fake);
    if (ref && typeof ref !== "function" && ref.current) {
      ref.current.value = "";
      ref.current.focus();
    }
  };

  const showClear = !disabled && !readOnly && value.length > 0;

  const shell = [
    "group relative flex min-h-[48px] w-full items-center gap-2 rounded-lg border px-3 transition-[border-color,background-color,box-shadow]",
    "border border-gray-300 bg-white text-gray-900",
    /* hover: لا نغيّر لون الحدود (كان يلغي الأخضر السفلي مع focus-within+hover) */
    "not-focus-within:hover:bg-gray-50",
    /* تركيز: خط سفلي أخضر ثابت حتى مع hover فوق الصندوق */
    "focus-within:border-b-4 focus-within:border-b-sa-600 focus-within:shadow-none",
    "focus-within:hover:border-b-sa-600",
    "active:bg-gray-100",
    disabled ? "pointer-events-none border-gray-200 bg-gray-100 text-gray-400" : "",
    readOnly ? "cursor-default border-gray-200 bg-gray-100" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={[shell, className].join(" ")} dir={dir}>
      <span className="pointer-events-none shrink-0 text-gray-800" aria-hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </span>

      <input
        ref={ref}
        id={inputId}
        name={name}
        type="text"
        autoComplete="off"
        inputMode="search"
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        value={isControlled ? valueProp : undefined}
        defaultValue={isControlled ? undefined : inner}
        onChange={handleChange}
        className={[
          "min-w-0 flex-1 border-0 bg-transparent py-2 text-base shadow-none placeholder:text-gray-500",
          /* إلغاء تركيز @tailwindcss/forms (حد/حلقة زرقاء) — التمييز من الحاوية فقط */
          "outline-none ring-0 focus:border-transparent focus:outline-none focus:shadow-none focus:ring-0 focus:ring-offset-0",
          "focus:!border-transparent focus:!shadow-none focus:!ring-0 focus:!ring-offset-0",
          "focus-visible:outline-none focus-visible:!shadow-none focus-visible:!ring-0",
          dir === "rtl" ? "text-right" : "text-left",
          disabled ? "text-gray-400" : "",
          readOnly ? "cursor-default" : "",
        ].join(" ")}
      />

      <div className="flex shrink-0 items-center gap-2">
        {showClear && (
          <>
            <button
              type="button"
              onClick={handleClear}
              className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
              aria-label="مسح"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
            <span className="h-6 w-px bg-gray-300" aria-hidden />
          </>
        )}
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          className={["rounded p-1 text-gray-700 hover:bg-gray-100", disabled ? "text-gray-300" : ""].join(" ")}
          aria-label={micAriaLabel}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>
      </div>
    </div>
  );
});

export default DgaSearchBox;
